import { useEffect, useState } from 'react'
import { Check, LayoutTemplate, Pencil, Trash2, X } from 'lucide-react'

import { Badge } from 'components/ui/badge'
import { Input } from 'components/ui/input'
import { Sheet } from 'components/ui/sheet'
import { Tabs, TabsList, TabsTrigger } from 'components/ui/tabs'
import { toast } from 'components/ui/toast-store'

import * as presentationService from '../services/presentationService'
import type { PresentationTemplate, TemplateKind } from '../types/presentation.types'
import { SCOPE_OPTIONS } from './AssetPicker'
import { sectionSummary } from './DeckTemplateGallery'

/**
 * Every template the user can use, of both kinds (MYD-604): deck templates
 * that make a whole deck, and story templates that add one story to a deck.
 * Each kind is split into the user's own, which they can rename and delete,
 * and the starters, which are read-only (the server refuses them anyway).
 */
interface ManageTemplatesSheetProps {
	open: boolean
	onClose: () => void
}

type Lists = Record<TemplateKind, PresentationTemplate[] | null>

const messageOf = (cause: any, fallback: string) => cause?.response?.data?.message ?? fallback

const scopeLabel = (template: PresentationTemplate) => SCOPE_OPTIONS.find(option => option.scope === template.scope)?.label ?? template.scope

export function ManageTemplatesSheet({ open, onClose }: ManageTemplatesSheetProps) {
	const [kind, setKind] = useState<TemplateKind>('deck')
	const [lists, setLists] = useState<Lists>({ deck: null, story: null })
	const [error, setError] = useState<string | null>(null)

	// Fresh every time it opens: a template saved from the editor since should show.
	useEffect(() => {
		if (!open) return
		let cancelled = false
		setLists({ deck: null, story: null })
		setError(null)
		Promise.all([presentationService.listDeckTemplates(), presentationService.listTemplates()])
			.then(([deck, story]) => !cancelled && setLists({ deck, story }))
			.catch(() => {
				if (cancelled) return
				setLists({ deck: [], story: [] })
				setError('Templates could not be loaded.')
			})
		return () => {
			cancelled = true
		}
	}, [open])

	const replace = (key: string, next: PresentationTemplate | null) =>
		setLists(current => ({
			...current,
			[kind]: (current[kind] ?? []).flatMap(template => (template.key !== key ? [template] : next ? [{ ...template, ...next }] : [])),
		}))

	const rename = async (template: PresentationTemplate, name: string) => {
		try {
			replace(template.key, await presentationService.renameTemplate(template.key, { name }))
			toast.success('Template renamed')
			return true
		} catch (cause) {
			toast.error(messageOf(cause, 'The template could not be renamed.'))
			return false
		}
	}

	const remove = async (template: PresentationTemplate) => {
		const confirmed = await toast.confirm({
			title: 'Delete this template?',
			message: `"${template.name}" will be deleted. Decks already made from it are not affected.`,
			confirmLabel: 'Delete',
			variant: 'destructive',
		})
		if (!confirmed) return
		try {
			await presentationService.deleteTemplate(template.key)
			replace(template.key, null)
			toast.success('Template deleted')
		} catch (cause) {
			toast.error(messageOf(cause, 'The template could not be deleted.'))
		}
	}

	const list = lists[kind]
	const mine = (list ?? []).filter(template => template.isOwner)
	const starters = (list ?? []).filter(template => template.isStarter)

	return (
		<Sheet open={open} onClose={onClose} side="right" className="w-[400px] sm:w-[480px]">
			<div className="flex h-14 shrink-0 items-center justify-between border-b border-gray-200 px-4">
				<h2 className="text-base font-semibold text-gray-900">Templates</h2>
				<button type="button" onClick={onClose} aria-label="Close" className="rounded p-1.5 text-gray-500 hover:bg-gray-100">
					<X className="h-4 w-4" />
				</button>
			</div>

			<div className="border-b border-gray-200 px-4 py-3">
				<Tabs value={kind} onValueChange={value => setKind(value as TemplateKind)}>
					<TabsList aria-label="Kind of template">
						<TabsTrigger value="deck">Decks</TabsTrigger>
						<TabsTrigger value="story">Stories</TabsTrigger>
					</TabsList>
				</Tabs>
				<p className="mt-2 text-xs text-muted-foreground">
					{kind === 'deck'
						? 'A deck template builds a whole deck for one asset. Save one from a deck’s editor.'
						: 'A story template adds one story to a deck. Save one from a story’s menu in the editor.'}
				</p>
			</div>

			<div className="flex-1 overflow-y-auto">
				{error && <p className="px-4 py-3 text-sm text-destructive">{error}</p>}
				{list === null ? (
					<p className="px-4 py-6 text-center text-sm text-muted-foreground">Loading templates...</p>
				) : (
					<>
						<TemplateSection title="Mine" empty="You have not saved any yet." templates={mine} onRename={rename} onDelete={remove} />
						<TemplateSection title="Starters" empty="None." templates={starters} />
					</>
				)}
			</div>
		</Sheet>
	)
}

interface TemplateSectionProps {
	title: string
	empty: string
	templates: PresentationTemplate[]
	/** Only the user's own are editable. */
	onRename?: (template: PresentationTemplate, name: string) => Promise<boolean>
	onDelete?: (template: PresentationTemplate) => void
}

function TemplateSection({ title, empty, templates, onRename, onDelete }: TemplateSectionProps) {
	return (
		<section className="border-b border-gray-200 px-4 py-3" aria-label={title}>
			<h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">{title}</h3>
			{templates.length === 0 ? (
				<p className="py-1 text-sm text-gray-500">{empty}</p>
			) : (
				<ul className="space-y-1">
					{templates.map(template => (
						<TemplateRow key={template.key} template={template} onRename={onRename} onDelete={onDelete} />
					))}
				</ul>
			)}
		</section>
	)
}

function TemplateRow({ template, onRename, onDelete }: { template: PresentationTemplate } & Pick<TemplateSectionProps, 'onRename' | 'onDelete'>) {
	const [editing, setEditing] = useState(false)
	const [name, setName] = useState(template.name)
	const [busy, setBusy] = useState(false)
	const trimmed = name.trim()
	const extra = sectionSummary(template)

	const save = async () => {
		if (!onRename || !trimmed || busy) return
		if (trimmed === template.name) return setEditing(false)
		setBusy(true)
		const saved = await onRename(template, trimmed)
		setBusy(false)
		if (saved) setEditing(false)
	}

	return (
		<li className="group flex items-start gap-2 rounded-md px-2 py-2 hover:bg-gray-50">
			<LayoutTemplate className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
			<div className="min-w-0 flex-1">
				{editing ? (
					<div className="flex items-center gap-1">
						<Input
							aria-label={`New name for ${template.name}`}
							value={name}
							maxLength={140}
							autoFocus
							onChange={event => setName(event.target.value)}
							onKeyDown={event => {
								if (event.key === 'Enter') save()
								if (event.key === 'Escape') {
									// Cancels the rename only, not the whole sheet.
									event.stopPropagation()
									setName(template.name)
									setEditing(false)
								}
							}}
							className="h-8 text-sm"
						/>
						<button type="button" onClick={save} disabled={!trimmed || busy} aria-label="Save name" className="rounded p-1.5 text-success hover:bg-gray-100 disabled:opacity-40">
							<Check className="h-4 w-4" />
						</button>
					</div>
				) : (
					<p className="truncate text-sm font-medium text-gray-900">{template.name}</p>
				)}
				<p className="text-[11px] text-muted-foreground">
					{scopeLabel(template)} · {template.slideCount} {template.slideCount === 1 ? 'slide' : 'slides'}
					{extra ? ` · ${extra}` : ''}
				</p>
			</div>
			{template.isStarter && <Badge variant="secondary" className="shrink-0 text-[10px]">Starter</Badge>}
			{template.isOwner && !editing && (
				<div className="flex shrink-0 items-center gap-0.5 opacity-60 group-hover:opacity-100">
					<button type="button" onClick={() => setEditing(true)} aria-label={`Rename ${template.name}`} className="rounded p-1.5 text-gray-600 hover:bg-gray-100">
						<Pencil className="h-3.5 w-3.5" />
					</button>
					<button type="button" onClick={() => onDelete?.(template)} aria-label={`Delete ${template.name}`} className="rounded p-1.5 text-destructive hover:bg-gray-100">
						<Trash2 className="h-3.5 w-3.5" />
					</button>
				</div>
			)}
		</li>
	)
}

export default ManageTemplatesSheet
